import { Component, Input } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { filter, switchMap } from 'rxjs/operators';

import {
  NoteTarget, ShiftInfoFragment, TaggableLogbookDetailsFragment, UnapplyCalendarEventResponseTagGQL, UnapplyEntityConnectionTagGQL,
  UnapplyEntityTagGQL, UserRole
} from 'generated/graphql';

import {
  ShiftAddOrUpdateDialog
} from 'app/field-shared/dialogs/shift-add-or-update/shift-add-or-update.dialog';
import { NoteDialogComponent } from 'app/note/dialogs/note-dialog/note-dialog.component';
import { getNoteTypeFromTagType } from 'app/note/helpers/note-types';
import { AccountRoleService } from 'auth/account-role.service';
import { ToastrService } from 'core/toastr.service';
import { ConfirmDialogComponent } from 'shared/components/confirm-dialog/confirm-dialog-component';
import { AttachDialogComponent } from 'shared/components/attach-dialog/attach-dialog-component';
import { TagFileAttachmentService } from 'field-shared/tag-file-attachment.service';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'app-applied-tag-row',
  templateUrl: './applied-tag-row.component.html',
  styleUrls: ['./applied-tag-row.component.scss']
})
export class AppliedTagRowComponent  {
  @Input() appliedTag: TaggableLogbookDetailsFragment;

  get attachmentsEnabled() {
    return this.appliedTag.tag?.category?.attachmentsEnabled;
  }

  get currentCampaignId() {
    return this._activatedRoute.snapshot.queryParams.campaignId;
  }

  constructor(
    private _dialog: MatDialog,
    private _activatedRoute: ActivatedRoute,
    private _tagAttachmentService: TagFileAttachmentService,
    private _unapplyCalendarEventResponseTag: UnapplyCalendarEventResponseTagGQL,
    private _unapplyEntityTag: UnapplyEntityTagGQL,
    private _unapplyEntityConnectionTag: UnapplyEntityConnectionTagGQL,
    private _toastrService: ToastrService,
    private _accountRoleService: AccountRoleService,
  ) {
  }


  /**
   * Checks if a tag is readonly
   *
   * @param appliedTag The applied tag
   */
  isReadOnly(appliedTag: TaggableLogbookDetailsFragment) {
    return appliedTag.tag?.category?.readonly;
  }

  hasAttachment(appliedTag: TaggableLogbookDetailsFragment) {
    return appliedTag.attachments.length > 0;
  }

  canEdit(appliedTag: TaggableLogbookDetailsFragment) {
    const readOnly = this.isReadOnly(appliedTag);
    if (!readOnly) {
      return true;
    }

    return this._accountRoleService.userHasMinRole(UserRole.Administrator);
  }

  removeTagClicked(appliedTag: TaggableLogbookDetailsFragment) {
    ConfirmDialogComponent.open(this._dialog, {
      data: {
        message: 'Are you sure you want to remove this field response?'
      }
    }).afterClosed().pipe(
      filter(confirmed => confirmed),
      switchMap(() => this._unapplyMutation(appliedTag))
    ).subscribe(() => {
      this._toastrService.success('Response removed.');
    });
  }

  addFileClicked(appliedTag: TaggableLogbookDetailsFragment) {
    AttachDialogComponent.open(this._dialog, {
      data: {
        attachableId: appliedTag.id,
        attachableType: appliedTag.__typename
      }
    });
  }

  removeFileClicked(appliedTag: TaggableLogbookDetailsFragment) {
    const file = appliedTag.attachments[0];
    const dialogRef = ConfirmDialogComponent.open(this._dialog, {
      data: {
        title: `Delete ${file.name}?`,
        message: `Are you sure you want to delete file "${file.name}" from the response?`,
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel'
      }
    });

    dialogRef.afterClosed().pipe(
      filter(proceed => proceed),
      switchMap(
        () => this._tagAttachmentService.deleteFileFromTaggableLogbook(
          file.id, appliedTag.id
        )
      )
    )
      .subscribe(
        () => {
          this._toastrService.success('File Removed');
        });
  }

  addNoteClicked(appliedTag: TaggableLogbookDetailsFragment) {
    NoteDialogComponent.open(this._dialog, {
      data: {
        noteType: getNoteTypeFromTagType(appliedTag.tag.tagType),
        // For universal tags the applied tag has no campaignId.
        // Use the current campaignId from the route instead to set context.
        campaignId: appliedTag.campaign?.id || this.currentCampaignId,
        hideNotes: true,
        targetId: appliedTag.id,
        targetType: NoteTarget.TaggableLogbook,
        heading: `Add a ${appliedTag?.tag.tagType}`
      }
    });
  }

  editShiftClicked(shift: ShiftInfoFragment) {
    ShiftAddOrUpdateDialog.open(this._dialog, {
      data: {
        shift
      }
    });
  }

  private _unapplyMutation(appliedTag) {
    const input = {
      taggableLogbookId: appliedTag.id,
      campaignId: appliedTag.campaign?.id || this.currentCampaignId
    };

    if (appliedTag.targetType === 'CalendarEventResponse') {
      return this._unapplyCalendarEventResponseTag.mutate({ input });
    }
    if (appliedTag.targetType === 'EntityConnection') {
      return this._unapplyEntityConnectionTag.mutate({ input });
    }

    return this._unapplyEntityTag.mutate({ input });
  }
}
